export enum PermissionType {
  MEDIA = 'MEDIA',
  MANAGEMENT = 'MANAGEMENT',
  INTERNAL = 'INTERNAL',
}

enum PermissionId {
  API_KEY_CREATE = 'API_KEY_CREATE',
  API_KEY_READ = 'API_KEY_READ',
  API_KEY_LIST = 'API_KEY_LIST',
  API_KEY_UPDATE = 'API_KEY_UPDATE',
  API_KEY_DELETE = 'API_KEY_DELETE',
  API_KEY_ADMIN = 'API_KEY_ADMIN',
  APP_LIST = 'APP_LIST',
  BILLING_SUBSCRIPTION_CHANGE = 'BILLING_SUBSCRIPTION_CHANGE',
  ROOM_JOIN = 'ROOM_JOIN',
  ROOM_PUBLISH = 'ROOM_PUBLISH',
  ROOM_SUBSCRIBE = 'ROOM_SUBSCRIBE',
  ROOM_PUBLISH_DATA = 'ROOM_PUBLISH_DATA',
  PARTICIPANT_UPDATE = 'PARTICIPANT_UPDATE',
  PARTICIPANT_DELETE = 'PARTICIPANT_DELETE',
  PARTICIPANT_MUTE = 'PARTICIPANT_MUTE',
  PARTICIPANT_UNMUTE = 'PARTICIPANT_UNMUTE',
  PARTICIPANT_ADMIN = 'PARTICIPANT_ADMIN',
  ACCOUNT_CREATE = 'ACCOUNT_CREATE',
  ACCOUNT_READ = 'ACCOUNT_READ',
  ACCOUNT_LIST = 'ACCOUNT_LIST',
  ACCOUNT_UPDATE = 'ACCOUNT_UPDATE',
  ACCOUNT_DELETE = 'ACCOUNT_DELETE',
  ACCOUNT_ADMIN = 'ACCOUNT_ADMIN',
  USER_CREATE = 'USER_CREATE',
  USER_READ = 'USER_READ',
  USER_LIST = 'USER_LIST',
  USER_UPDATE = 'USER_UPDATE',
  USER_DELETE = 'USER_DELETE',
  USER_ADMIN = 'USER_ADMIN',
  USER_ASSIGN_ROLE = 'USER_ASSIGN_ROLE',
  USER_UNASSIGN_ROLE = 'USER_UNASSIGN_ROLE',
  CONSOLE_USER_ADMIN = 'CONSOLE_USER_ADMIN',
  ROOM_CREATE = 'ROOM_CREATE',
  ROOM_READ = 'ROOM_READ',
  ROOM_LIST = 'ROOM_LIST',
  ROOM_UPDATE = 'ROOM_UPDATE',
  ROOM_DELETE = 'ROOM_DELETE',
  ROOM_ADMIN = 'ROOM_ADMIN',
  ROLE_CREATE = 'ROLE_CREATE',
  ROLE_READ = 'ROLE_READ',
  ROLE_LIST = 'ROLE_LIST',
  ROLE_UPDATE = 'ROLE_UPDATE',
  ROLE_DELETE = 'ROLE_DELETE',
  ROLE_ADMIN = 'ROLE_ADMIN',
  DEFAULT_ROLE_STRATEGY_CREATE = 'DEFAULT_ROLE_STRATEGY_CREATE',
  DEFAULT_ROLE_STRATEGY_READ = 'DEFAULT_ROLE_STRATEGY_READ',
  DEFAULT_ROLE_STRATEGY_LIST = 'DEFAULT_ROLE_STRATEGY_LIST',
  DEFAULT_ROLE_STRATEGY_UPDATE = 'DEFAULT_ROLE_STRATEGY_UPDATE',
  DEFAULT_ROLE_STRATEGY_DELETE = 'DEFAULT_ROLE_STRATEGY_DELETE',
  DEFAULT_ROLE_STRATEGY_ADMIN = 'DEFAULT_ROLE_STRATEGY_ADMIN',
  DEPLOYMENT_LIST = 'DEPLOYMENT_LIST',
  OVERRIDE_CREATE = 'OVERRIDE_CREATE',
  OVERRIDE_DELETE = 'OVERRIDE_DELETE',
  OVERRIDE_ADMIN = 'OVERRIDE_ADMIN',
  AI_FEATURE_LIST = 'AI_FEATURE_LIST',
  AI_FEATURE_CREATE = 'AI_FEATURE_CREATE',
  AI_FEATURE_READ = 'AI_FEATURE_READ',
  AI_FEATURE_UPDATE = 'AI_FEATURE_UPDATE',
  AI_FEATURE_DELETE = 'AI_FEATURE_DELETE',
  AI_FEATURE_ADMIN = 'AI_FEATURE_ADMIN',
  MFA_READ = 'MFA_READ',
  PERMISSION_LIST = 'PERMISSION_LIST',
  INTEGRATION_MANAGE_EMAIL = 'INTEGRATION_MANAGE_EMAIL',
  DASHBOARD_LIST = 'DASHBOARD_LIST',
  CROSS_CLIENT_QUERY = 'CROSS_CLIENT_QUERY',
  ZOCKS_ADMIN = 'ZOCKS_ADMIN',
}

export class PermissionInfo {
  readonly id: string
  readonly type: PermissionType
  readonly description: string

  constructor(permission: [string, PermissionType, string]) {
    this.id = permission[0]
    this.type = permission[1]
    this.description = permission[2]
  }
}

export const Permission: Record<keyof typeof PermissionId, PermissionInfo> = {
  [PermissionId.API_KEY_ADMIN]: new PermissionInfo(['apikey:admin', PermissionType.MEDIA, 'Manage Api Keys']),
  [PermissionId.API_KEY_CREATE]: new PermissionInfo(['apikey:create', PermissionType.MEDIA, 'Create an Api Key']),
  [PermissionId.API_KEY_DELETE]: new PermissionInfo(['apikey:delete', PermissionType.MEDIA, 'Delete an Api Key']),
  [PermissionId.API_KEY_READ]: new PermissionInfo(['apikey:read', PermissionType.MEDIA, 'Read an Api Key']),
  [PermissionId.API_KEY_LIST]: new PermissionInfo(['apikey:list', PermissionType.MEDIA, 'List Api Keys']),
  [PermissionId.API_KEY_UPDATE]: new PermissionInfo(['apikey:update', PermissionType.MEDIA, 'Update an Api Key']),

  [PermissionId.APP_LIST]: new PermissionInfo(['app:list', PermissionType.MEDIA, 'List Apps']),

  [PermissionId.BILLING_SUBSCRIPTION_CHANGE]: new PermissionInfo(['billing_subscription:change', PermissionType.MANAGEMENT, 'Change billing subscription']),

  [PermissionId.ROOM_JOIN]: new PermissionInfo(['room:join', PermissionType.MEDIA, 'Join a room']),
  [PermissionId.ROOM_PUBLISH]: new PermissionInfo(['room:publish', PermissionType.MEDIA, 'Publish a message to a room']),
  [PermissionId.ROOM_SUBSCRIBE]: new PermissionInfo(['room:subscribe', PermissionType.MEDIA, 'Subscribe to a room']),
  [PermissionId.ROOM_PUBLISH_DATA]: new PermissionInfo(['room:publish_data', PermissionType.MEDIA, 'Publish data to a room']),

  [PermissionId.PARTICIPANT_UPDATE]: new PermissionInfo(['participant:update', PermissionType.MEDIA, 'Update a participant']),
  [PermissionId.PARTICIPANT_DELETE]: new PermissionInfo(['participant:delete', PermissionType.MEDIA, 'Delete a participant']),
  [PermissionId.PARTICIPANT_MUTE]: new PermissionInfo(['participant:mute', PermissionType.MEDIA, 'Mute a participant']),
  [PermissionId.PARTICIPANT_UNMUTE]: new PermissionInfo(['participant:unmute', PermissionType.MEDIA, 'Unmute a participant']),
  [PermissionId.PARTICIPANT_ADMIN]: new PermissionInfo(['participant:admin', PermissionType.MEDIA, 'Administer a participant']),

  [PermissionId.ACCOUNT_CREATE]: new PermissionInfo(['account:create', PermissionType.MANAGEMENT, 'Create an account']),
  [PermissionId.ACCOUNT_READ]: new PermissionInfo(['account:read', PermissionType.MANAGEMENT, 'Read an account']),
  [PermissionId.ACCOUNT_LIST]: new PermissionInfo(['account:list', PermissionType.MANAGEMENT, 'List accounts']),
  [PermissionId.ACCOUNT_UPDATE]: new PermissionInfo(['account:update', PermissionType.MANAGEMENT, 'Update an account']),
  [PermissionId.ACCOUNT_DELETE]: new PermissionInfo(['account:delete', PermissionType.MANAGEMENT, 'Delete an account']),
  [PermissionId.ACCOUNT_ADMIN]: new PermissionInfo(['account:admin', PermissionType.MANAGEMENT, 'Administer an account']),

  [PermissionId.USER_CREATE]: new PermissionInfo(['user:create', PermissionType.MANAGEMENT, 'Create a user']),
  [PermissionId.USER_READ]: new PermissionInfo(['user:read', PermissionType.MANAGEMENT, 'Read a user']),
  [PermissionId.USER_LIST]: new PermissionInfo(['user:list', PermissionType.MANAGEMENT, 'List users']),
  [PermissionId.USER_UPDATE]: new PermissionInfo(['user:update', PermissionType.MANAGEMENT, 'Update a user']),
  [PermissionId.USER_DELETE]: new PermissionInfo(['user:delete', PermissionType.MANAGEMENT, 'Delete a user']),
  [PermissionId.USER_ADMIN]: new PermissionInfo(['user:admin', PermissionType.MANAGEMENT, 'Administer a user']),
  [PermissionId.USER_ASSIGN_ROLE]: new PermissionInfo(['user:assign_role', PermissionType.MANAGEMENT, 'Assign a role to a user']),
  [PermissionId.USER_UNASSIGN_ROLE]: new PermissionInfo(['user:unassign_role', PermissionType.MANAGEMENT, 'Unassign a role from a user']),

  [PermissionId.CONSOLE_USER_ADMIN]: new PermissionInfo(['consoleuser:admin', PermissionType.MANAGEMENT, 'Administer a console user']),

  [PermissionId.ROOM_CREATE]: new PermissionInfo(['room:create', PermissionType.MANAGEMENT, 'Create a room']),
  [PermissionId.ROOM_READ]: new PermissionInfo(['room:read', PermissionType.MANAGEMENT, 'Read a room']),
  [PermissionId.ROOM_LIST]: new PermissionInfo(['room:list', PermissionType.MANAGEMENT, 'List rooms']),
  [PermissionId.ROOM_UPDATE]: new PermissionInfo(['room:update', PermissionType.MANAGEMENT, 'Update a room']),
  [PermissionId.ROOM_DELETE]: new PermissionInfo(['room:delete', PermissionType.MANAGEMENT, 'Delete a room']),
  [PermissionId.ROOM_ADMIN]: new PermissionInfo(['room:admin', PermissionType.MANAGEMENT, 'Administer a room']),

  [PermissionId.ROLE_CREATE]: new PermissionInfo(['role:create', PermissionType.MANAGEMENT, 'Create a role']),
  [PermissionId.ROLE_READ]: new PermissionInfo(['role:read', PermissionType.MANAGEMENT, 'Read a role']),
  [PermissionId.ROLE_LIST]: new PermissionInfo(['role:list', PermissionType.MANAGEMENT, 'List roles']),
  [PermissionId.ROLE_UPDATE]: new PermissionInfo(['role:update', PermissionType.MANAGEMENT, 'Update a role']),
  [PermissionId.ROLE_DELETE]: new PermissionInfo(['role:delete', PermissionType.MANAGEMENT, 'Delete a role']),
  [PermissionId.ROLE_ADMIN]: new PermissionInfo(['role:admin', PermissionType.MANAGEMENT, 'Administer a role']),

  [PermissionId.DEFAULT_ROLE_STRATEGY_CREATE]: new PermissionInfo(['drs:create', PermissionType.MANAGEMENT, 'Create a default role strategy']),
  [PermissionId.DEFAULT_ROLE_STRATEGY_READ]: new PermissionInfo(['drs:read', PermissionType.MANAGEMENT, 'Read a default role strategy']),
  [PermissionId.DEFAULT_ROLE_STRATEGY_LIST]: new PermissionInfo(['drs:list', PermissionType.MANAGEMENT, 'List default role strategies']),
  [PermissionId.DEFAULT_ROLE_STRATEGY_UPDATE]: new PermissionInfo(['drs:update', PermissionType.MANAGEMENT, 'Update a default role strategy']),
  [PermissionId.DEFAULT_ROLE_STRATEGY_DELETE]: new PermissionInfo(['drs:delete', PermissionType.MANAGEMENT, 'Delete a default role strategy']),
  [PermissionId.DEFAULT_ROLE_STRATEGY_ADMIN]: new PermissionInfo(['drs:admin', PermissionType.MANAGEMENT, 'Administer a default role strategy']),

  [PermissionId.DEPLOYMENT_LIST]: new PermissionInfo(['deployment:list', PermissionType.MANAGEMENT, 'List Deployments']),

  [PermissionId.OVERRIDE_CREATE]: new PermissionInfo(['override:create', PermissionType.MANAGEMENT, 'Create an override']),
  [PermissionId.OVERRIDE_DELETE]: new PermissionInfo(['override:delete', PermissionType.MANAGEMENT, 'Delete overrides']),
  [PermissionId.OVERRIDE_ADMIN]: new PermissionInfo(['override:admin', PermissionType.MANAGEMENT, 'Administer overrides']),

  [PermissionId.AI_FEATURE_CREATE]: new PermissionInfo(['aifeature:create', PermissionType.MANAGEMENT, 'Create an ai feature']),
  [PermissionId.AI_FEATURE_READ]: new PermissionInfo(['aifeature:read', PermissionType.MANAGEMENT, 'Read an ai feature']),
  [PermissionId.AI_FEATURE_LIST]: new PermissionInfo(['aifeature:list', PermissionType.MANAGEMENT, 'List ai features']),
  [PermissionId.AI_FEATURE_UPDATE]: new PermissionInfo(['aifeature:update', PermissionType.MANAGEMENT, 'Update an ai feature']),
  [PermissionId.AI_FEATURE_DELETE]: new PermissionInfo(['aifeature:delete', PermissionType.MANAGEMENT, 'Delete an ai feature']),
  [PermissionId.AI_FEATURE_ADMIN]: new PermissionInfo(['aifeature:admin', PermissionType.MANAGEMENT, 'Administer ai features']),

  [PermissionId.MFA_READ]: new PermissionInfo(['mfa:read', PermissionType.MANAGEMENT, 'Read MFA']),

  [PermissionId.PERMISSION_LIST]: new PermissionInfo(['permission:list', PermissionType.MANAGEMENT, 'List permissions']),

  [PermissionId.INTEGRATION_MANAGE_EMAIL]: new PermissionInfo(['integration:manage_email', PermissionType.MANAGEMENT, 'Manage email integration']),
  [PermissionId.DASHBOARD_LIST]: new PermissionInfo(['dashboard:list', PermissionType.MANAGEMENT, 'List dashboards']),
  [PermissionId.CROSS_CLIENT_QUERY]: new PermissionInfo(['cross_client_query:execute', PermissionType.MANAGEMENT, 'Execute cross client query']),

  [PermissionId.ZOCKS_ADMIN]: new PermissionInfo(['zocks:admin', PermissionType.INTERNAL, 'Zocks service admin permission']),
}
